import clsx from 'clsx';
import { Dialog, Transition } from '@headlessui/react';
import { ShoppingCartIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import LoadingDots from '../loading-dots';
import Price from '../price';
import { DEFAULT_OPTION } from 'lib/constants';
import { createUrl } from '../../utils';
import Image from '../Image';
import { createCartAndSetCookie } from './actions';
import { useCart } from './cart-context';
import { buildPayLinkCheckoutUrl, getPayLinkCheckoutType } from 'lib/paylink';
import { DeleteItemButton } from './delete-item-button';
import { EditItemQuantityButton } from './edit-item-quantity-button';
import OpenCart from './open-cart';
import { supabase, type UserProfile } from '../../lib/supabase';
import { Package, MapPin, Briefcase, LogOut, User } from 'lucide-react';

type MerchandiseSearchParams = {
  [key: string]: string;
};

export default function CartModal() {
  const { cart } = useCart();
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const navigate = useNavigate();
  const quantityRef = useRef(cart?.totalQuantity);
  const openCart = () => setIsOpen(true);
  const closeCart = () => setIsOpen(false);

  useEffect(() => {
    if (!cart) {
      createCartAndSetCookie();
    }
  }, [cart]);

  useEffect(() => {
    // Vérifier la session utilisateur
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
        // Récupérer le profil utilisateur
        const { data: profileData } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();
        if (profileData) {
          setProfile(profileData);
        }
      } else {
        setUser(null);
        setProfile(null);
      }
    };

    checkUser();

    // Écouter les changements de session
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event: any, session: any) => {
      if (session?.user) {
        setUser(session.user);
        // Récupérer le profil utilisateur
        supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single()
          .then(({ data }) => {
            if (data) {
              setProfile(data);
            }
          });
      } else {
        setUser(null);
        setProfile(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      closeCart();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  useEffect(() => {
    if (
      cart?.totalQuantity &&
      cart?.totalQuantity !== quantityRef.current &&
      cart?.totalQuantity > 0
    ) {
      if (!isOpen) {
        setIsOpen(true);
      }
      quantityRef.current = cart?.totalQuantity;
    }
  }, [isOpen, cart?.totalQuantity, quantityRef]);

  return (
    <>
      <button aria-label="Open cart" onClick={openCart}>
        <OpenCart quantity={cart?.totalQuantity} />
      </button>
      <Transition show={isOpen}>
        <Dialog onClose={closeCart} className="relative z-50">
          <Transition.Child
            as={Fragment}
            enter="transition-all ease-in-out duration-300"
            enterFrom="opacity-0 backdrop-blur-none"
            enterTo="opacity-100 backdrop-blur-[.5px]"
            leave="transition-all ease-in-out duration-200"
            leaveFrom="opacity-100 backdrop-blur-[.5px]"
            leaveTo="opacity-0 backdrop-blur-none"
          >
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="transition-all ease-in-out duration-300"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transition-all ease-in-out duration-200"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <Dialog.Panel className="fixed bottom-0 right-0 top-0 flex h-full w-full flex-col border-l border-neutral-200 bg-white/80 p-6 text-black backdrop-blur-xl md:w-[390px] dark:border-neutral-700 dark:bg-black/80 dark:text-white">
              {/* Section Mon compte ou Se connecter - Mobile uniquement */}
              <div className="mb-4 md:hidden">
                {user ? (
                  <UserAccountSection 
                    user={user} 
                    profile={profile} 
                    onLinkClick={closeCart}
                    onSignOut={handleSignOut}
                  />
                ) : (
                  <Link
                    to="/login"
                    onClick={closeCart}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#ff0442] to-[#ff1a4d] px-4 py-3 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#ff0442] focus:ring-offset-2"
                  >
                    Se connecter
                  </Link>
                )}
              </div>
              <div className="flex items-center justify-between">
                <p className="text-lg font-semibold">Mon panier</p>
                <button aria-label="Close cart" onClick={closeCart}>
                  <CloseCart />
                </button>
              </div>

              {!cart || cart.lines.length === 0 ? (
                <div className="mt-20 flex w-full flex-col items-center justify-center overflow-hidden">
                  <ShoppingCartIcon className="h-16" />
                  <p className="mt-6 text-center text-2xl font-bold">
                    Votre panier est vide.
                  </p>
                </div>
              ) : (
                <div className="flex h-full flex-col justify-between overflow-hidden p-1">
                  <ul className="grow overflow-auto py-4">
                    {cart.lines
                      .sort((a, b) =>
                        a.merchandise.product.title.localeCompare(
                          b.merchandise.product.title
                        )
                      )
                      .map((item, i) => {
                        const merchandiseSearchParams =
                          {} as MerchandiseSearchParams;

                        item.merchandise.selectedOptions.forEach(
                          ({ name, value }) => {
                            if (value !== DEFAULT_OPTION) {
                              merchandiseSearchParams[name.toLowerCase()] =
                                value;
                            }
                          }
                        );

                        const merchandiseUrl = createUrl(
                          `/product/${item.merchandise.product.handle}`,
                          new URLSearchParams(merchandiseSearchParams)
                        );

                        return (
                          <li
                            key={i}
                            className="flex w-full flex-col border-b border-neutral-300 dark:border-neutral-700"
                          >
                            <div className="relative flex w-full flex-row justify-between px-1 py-4">
                              <div className="absolute z-40 -ml-1 -mt-2">
                                <DeleteItemButton item={item} />
                              </div>
                              <div className="flex flex-row">
                                <div className="relative h-16 w-16 overflow-hidden rounded-md border border-neutral-300 bg-neutral-300 dark:border-neutral-700 dark:bg-neutral-900 dark:hover:bg-neutral-800">
                                  <Image
                                    className="h-full w-full object-cover"
                                    width={64}
                                    height={64}
                                    alt={
                                      item.merchandise.product.featuredImage
                                        .altText ||
                                      item.merchandise.product.title
                                    }
                                    src={
                                      item.merchandise.product.featuredImage.url
                                    }
                                  />
                                </div>
                                <Link
                                  to={merchandiseUrl}
                                  onClick={closeCart}
                                  className="z-30 ml-2 flex flex-row space-x-4"
                                >
                                  <div className="flex flex-1 flex-col text-base">
                                    <span className="leading-tight">
                                      {item.merchandise.product.title}
                                    </span>
                                    {item.merchandise.title !==
                                    DEFAULT_OPTION ? (
                                      <p className="text-sm text-neutral-500 dark:text-neutral-400">
                                        {item.merchandise.title}
                                      </p>
                                    ) : null}
                                  </div>
                                </Link>
                              </div>
                              <div className="flex h-16 flex-col justify-between">
                                <Price
                                  className="flex justify-end space-y-2 text-right text-sm"
                                  amount={item.cost.totalAmount.amount}
                                  currencyCode={
                                    item.cost.totalAmount.currencyCode
                                  }
                                />
                                <div className="ml-auto flex h-9 flex-row items-center rounded-full border border-neutral-200 dark:border-neutral-700">
                                  <EditItemQuantityButton
                                    item={item}
                                    type="minus"
                                  />
                                  <p className="w-6 text-center">
                                    <span className="w-full text-sm">
                                      {item.quantity}
                                    </span>
                                  </p>
                                  <EditItemQuantityButton
                                    item={item}
                                    type="plus"
                                  />
                                </div>
                              </div>
                            </div>
                          </li>
                        );
                      })}
                  </ul>
                  <div className="py-4 text-sm text-neutral-500 dark:text-neutral-400">
                    <div className="mb-3 flex items-center justify-between border-b border-neutral-200 pb-1 dark:border-neutral-700">
                      <p>Taxes</p>
                      <Price
                        className="text-right text-base text-black dark:text-white"
                        amount={cart.cost.totalTaxAmount.amount}
                        currencyCode={cart.cost.totalTaxAmount.currencyCode}
                      />
                    </div>
                    <div className="mb-3 flex items-center justify-between border-b border-neutral-200 pb-1 pt-1 dark:border-neutral-700">
                      <p>Livraison</p>
                      <p className="text-right">Calculé à la caisse</p>
                    </div>
                    <div className="mb-3 flex items-center justify-between border-b border-neutral-200 pb-1 pt-1 dark:border-neutral-700">
                      <p>Total</p>
                      <Price
                        className="text-right text-base text-black dark:text-white"
                        amount={cart.cost.totalAmount.amount}
                        currencyCode={cart.cost.totalAmount.currencyCode}
                      />
                    </div>
                  </div>
                  <CheckoutButton />
                </div>
              )}
            </Dialog.Panel>
          </Transition.Child>
        </Dialog>
      </Transition>
    </>
  );
}

function UserAccountSection({ 
  user, 
  profile, 
  onLinkClick, 
  onSignOut 
}: { 
  user: any; 
  profile: UserProfile | null;
  onLinkClick: () => void;
  onSignOut: () => void;
}) {
  const firstName = profile?.first_name || user.email?.split('@')[0] || 'Utilisateur';
  const avatarUrl = profile?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(firstName)}&background=ff0442&color=fff&size=128`;
  
  // Récupérer le username pour le lien vers le profil
  const username = profile?.username || user.id;

  return (
    <div className="rounded-xl border border-neutral-200 bg-white/95 backdrop-blur-sm dark:border-neutral-700 dark:bg-neutral-800/95">
      {/* Header avec profil */}
      <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-700">
        <Link 
          to={`/user/${username}`}
          onClick={onLinkClick}
          className="flex items-center gap-3"
        >
          <img
            src={avatarUrl}
            alt={firstName}
            className="h-10 w-10 rounded-full object-cover ring-2 ring-neutral-200 dark:ring-neutral-700"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">
              {firstName} {profile?.last_name || ''}
            </p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
              {user.email}
            </p>
          </div>
          <User className="h-5 w-5 text-neutral-400" />
        </Link>
      </div>

      {/* Menu items */}
      <div className="p-2">
        <Link
          to="/mes-commandes"
          onClick={onLinkClick}
          className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left transition-all duration-200 hover:bg-neutral-50 dark:hover:bg-neutral-700/50"
        >
          <Package className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
          <span className="text-sm font-medium text-neutral-800 dark:text-neutral-100">
            Mes commandes
          </span>
        </Link>

        <Link
          to="/mon-adresse"
          onClick={onLinkClick}
          className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left transition-all duration-200 hover:bg-neutral-50 dark:hover:bg-neutral-700/50"
        >
          <MapPin className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
          <span className="text-sm font-medium text-neutral-800 dark:text-neutral-100">
            Mon adresse
          </span>
        </Link>

        <Link
          to="/espace-business"
          onClick={onLinkClick}
          className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left transition-all duration-200 hover:bg-neutral-50 dark:hover:bg-neutral-700/50"
        >
          <Briefcase className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
          <span className="text-sm font-medium text-neutral-800 dark:text-neutral-100">
            Espace Business
          </span>
        </Link>

        {/* Séparateur */}
        <div className="border-t border-neutral-200 dark:border-neutral-700 my-1" />

        {/* Déconnexion */}
        <button
          onClick={onSignOut}
          className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left transition-all duration-200 hover:bg-red-50 dark:hover:bg-red-900/20"
        >
          <LogOut className="h-5 w-5 text-red-600 dark:text-red-400" />
          <span className="text-sm font-medium text-red-600 dark:text-red-400">
            Se déconnecter
          </span>
        </button>
      </div>
    </div>
  );
}

function CloseCart({ className }: { className?: string }) {
  return (
    <div className="relative flex h-11 w-11 items-center justify-center rounded-md border border-neutral-200 text-black transition-colors dark:border-neutral-700 dark:text-white">
      <XMarkIcon
        className={clsx(
          'h-6 transition-all ease-in-out hover:scale-110',
          className
        )}
      />
    </div>
  );
}

function CheckoutButton() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { cart } = useCart();

  // Détection checkout business (1 marchand) vs multi-vendeurs (plusieurs marchands) — logique dans lib/paylink
  const checkoutType = getPayLinkCheckoutType(cart);

  const handleCheckout = async () => {
    setLoading(true);
    // Redirection checkout Shoplink (paypool) avec tout le panier
    if (cart) {
      const payLinkUrl = buildPayLinkCheckoutUrl(cart, 0);
      if (payLinkUrl) {
        window.location.href = payLinkUrl;
        return;
      }
    }
    navigate('/checkout');
    setLoading(false);
  };

  const buttonLabel =
    checkoutType === 'multi'
      ? 'Passer à la caisse (plusieurs boutiques)'
      : 'Passer à la caisse';

  return (
    <button
      className="block w-full rounded-full bg-gradient-to-r from-[#ff0442] to-[#ff1a4d] p-3 text-center text-sm font-medium text-white opacity-90 hover:opacity-100 disabled:opacity-50 transition-all hover:shadow-lg"
      onClick={handleCheckout}
      disabled={loading}
    >
      {loading ? <LoadingDots className="bg-white" /> : buttonLabel}
    </button>
  );
}



